import { HGInterval, HGIntervalType } from "@/common/types";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { copyStack, generateStack, HGAnchor, HGStack } from "./stack";
import { intervalsAreEqual } from "@/common/intervals";
import { MapIntervals } from "@/engine/engine";

/** Which endpoint of the range the UI is currently editing. */
export type HGFocus = "start" | "end";

export class HGStacks {
  #stack: HGStack;
  #endStack: HGStack | null;
  // The endpoint the picker currently edits. Lives here rather than in React
  // state because three sibling components need it and share only this
  // instance, and because "no end stack implies focus is start" is an
  // invariant of *this* object, broken from four places inside it.
  // Deliberately NOT serialized by save(): that output drives sessionStorage,
  // document.title and router.navigate(), and merely focusing a chip must not
  // push a history entry.
  #focus: HGFocus;
  #name: string;
  #updateCounter: number;
  #listeners: Set<() => void>;

  constructor(stack: HGStack, endStack: HGStack | null) {
    this.#name = "hi-graphy";
    this.#updateCounter = 0;
    this.#stack = stack;
    this.#endStack = endStack;
    this.#focus = "start";
    this.#listeners = new Set<() => void>();

    this.#onStackChange();
  }

  load(stacksData: string) {
    const stacksInfo = JSON.parse(stacksData);
    const stackInfo = stacksInfo["stack"];
    const endStackInfo = stacksInfo["endStack"];
    // Assign both fields before notifying: going through setStack() first
    // would run #updateMinMax() against the *previous* end stack, whose clamps
    // can silently move the interval we just loaded.
    this.#stack = new HGStack(
      BigInt(stackInfo["n"]),
      stackInfo["it"],
      stackInfo["its"],
    );
    this.#endStack = endStackInfo
      ? new HGStack(
          BigInt(endStackInfo["n"]),
          endStackInfo["it"],
          endStackInfo["its"],
        )
      : null;
    this.#focus = "start";
    this.#onStackChange();
  }

  save() {
    interface StackData {
      n: string;
      it: HGIntervalType;
      its: string[];
    }
    const data = {
      stack: {
        n: this.#stack.getInterval().n.toString(),
        it: this.#stack.getInterval().it,
        its: this.#stack.getParentIts(),
      },
      endStack: null,
    } as { stack: StackData; endStack: StackData | null };

    if (this.#endStack) {
      data["endStack"] = {
        n: this.#endStack.getInterval().n.toString(),
        it: this.#endStack.getInterval().it,
        its: this.#endStack.getParentIts(),
      };
    }

    return JSON.stringify(data);
  }

  #onStackChange() {
    ++this.#updateCounter;
    if (
      this.#endStack &&
      intervalsAreEqual(this.#stack.getInterval(), this.#endStack.getInterval())
    ) {
      console.warn(
        "End stack interval is equal to stack interval. End stack will be set to null.",
      );
      this.setEndStack(null);
    }
    // There is nothing to focus when the selection is a single interval.
    if (!this.#endStack) this.#focus = "start";
    this.#updateName();
    this.#updateMinMax();
    this.#notify();
  }

  // #min/#max are expressed in units of each stack's own `it`, so they are
  // stale the instant a level changes and would reject an n the new pair
  // allows. Every operation that can move or re-level an endpoint clears them
  // first and lets #updateMinMax() re-derive them (in seconds) afterwards.
  #clearClamps() {
    this.#stack.setMaxBeginSecond(undefined);
    this.#endStack?.setMinEndSecond(undefined);
  }

  // Notify without recomputing derived state, for changes (focus) that alter
  // no interval.
  #touch() {
    ++this.#updateCounter;
    this.#notify();
  }

  getUpdateCounter() {
    return this.#updateCounter;
  }

  getInterval() {
    return this.#stack.getInterval();
  }

  getIntervalAPI() {
    return this.#stack.getIntervalAPI();
  }

  getEndInterval() {
    return this.#endStack ? this.#endStack.getInterval() : null;
  }

  getEndIntervalAPI() {
    return this.#endStack ? this.#endStack.getIntervalAPI() : null;
  }

  setStack(stack: HGStack) {
    this.#stack = stack;
    this.#onStackChange();
  }

  setEndStack(endStack: HGStack | null) {
    this.#endStack = endStack;
    this.#onStackChange();
  }

  /**
   * Replaces the whole selection. The single entry point for "go somewhere
   * else entirely" (quick-interval presets, navigating from the records view,
   * tapping a group header), so resetting focus here covers all of them.
   */
  setIntervals(interval: HGInterval, endInterval: HGInterval | null) {
    this.#clearClamps();

    if (endInterval) {
      if (this.#endStack) {
        this.#endStack.setInterval(endInterval);
      } else {
        this.#endStack = generateStack(endInterval);
      }
    } else {
      this.#endStack = null;
    }
    this.#stack.setInterval(interval);
    this.#focus = "start";
    this.#onStackChange();
  }

  // --- Focus -------------------------------------------------------------
  //
  // The two endpoints are independent: each carries its own interval type and
  // its own parent chain, so a range can run from a year to a minute. The
  // picker edits whichever one is focused and leaves the other alone.

  getFocus(): HGFocus {
    return this.#endStack ? this.#focus : "start";
  }

  setFocus(focus: HGFocus) {
    const next = this.#endStack ? focus : "start";
    if (next === this.#focus) return;
    this.#focus = next;
    this.#touch();
  }

  getFocusedStack(): HGStack {
    return this.#focus === "end" && this.#endStack
      ? this.#endStack
      : this.#stack;
  }

  getFocusedInterval(): HGInterval {
    return this.getFocusedStack().getInterval();
  }

  /** The endpoint that is *not* focused; null when there is no range. */
  getOtherStack(): HGStack | null {
    if (!this.#endStack) return null;
    return this.#focus === "end" ? this.#stack : this.#endStack;
  }

  getOtherInterval(): HGInterval | null {
    return this.getOtherStack()?.getInterval() ?? null;
  }

  /** "begin" for the start endpoint, "end" for the end one. See HGStack.push(). */
  #focusAnchor(): HGAnchor {
    return this.getFocus() === "end" ? "end" : "begin";
  }

  /**
   * Moves the focused endpoint. Never throws: if the user picks a position
   * past the other endpoint the two swap, the ordinary date-picker idiom.
   * Clamping instead would make a tapped row appear to do nothing, which reads
   * as a broken control on touch.
   */
  setFocusedInterval(interval: HGInterval) {
    this.#setEndpointInterval(this.getFocus(), interval);
  }

  /** Moves the endpoint that is not focused (desktop shift-click shortcut). */
  setOtherInterval(interval: HGInterval) {
    if (!this.#endStack) return;
    this.#setEndpointInterval(
      this.#focus === "start" ? "end" : "start",
      interval,
    );
  }

  #setEndpointInterval(target: HGFocus, interval: HGInterval) {
    this.#clearClamps();

    if (!this.#endStack) {
      this.#stack.setInterval(interval);
      this.#onStackChange();
      return;
    }

    // Ordering is a question about covered time, not about n: the two
    // endpoints can sit at different levels, where n is not comparable.
    const probe = generateStack(interval);
    const targetStart = target === "start";
    const beginSecond = (targetStart ? probe : this.#stack).getBeginSecond();
    const endSecond = (targetStart ? this.#endStack : probe).getEndSecond();

    if (beginSecond > endSecond) {
      if (targetStart) {
        this.#stack = this.#endStack;
        this.#endStack = probe;
      } else {
        this.#endStack = this.#stack;
        this.#stack = probe;
      }
      // Either way the stack the user was editing has moved to the other slot
      // — whether they dragged the focused handle past the other one, or
      // shift-clicked the other one past the focused one — so focus follows it.
      this.#focus = this.#focus === "start" ? "end" : "start";
    } else if (targetStart) {
      this.#stack.setInterval(interval);
    } else {
      this.#endStack.setInterval(interval);
    }
    this.#onStackChange();
  }

  /**
   * Re-expresses the focused endpoint at another level, keeping its anchored
   * edge fixed — switching an end from years to months must land on December,
   * not January.
   */
  setFocusedIt(it: HGIntervalType) {
    const current = this.getFocusedInterval();
    if (current.it === it) return;
    const span = MapIntervals(current, null, it);
    const n = this.#focusAnchor() === "end" ? span.end.n : span.begin.n;
    this.setFocusedInterval({ n, it });
  }

  /** Drills the focused endpoint one level down, leaving the other alone. */
  pushFocused(it?: HGIntervalType) {
    const anchor = this.#focusAnchor();
    const focused = this.getFocusedStack();
    this.#clearClamps();
    // Nothing below the bottom of the hierarchy — leave the stacks untouched.
    if (!focused.push(it, anchor)) {
      this.#updateMinMax();
      return;
    }
    this.#onStackChange();
  }

  /** Moves the focused endpoint one level up. */
  popFocused() {
    const anchor = this.#focusAnchor();
    const focused = this.getFocusedStack();
    this.#clearClamps();
    if (!focused.pop(anchor)) {
      this.#updateMinMax();
      return;
    }
    this.#onStackChange();
  }

  shiftFocused(n: number) {
    if (!this.getFocusedStack().shift(n)) return;
    this.#onStackChange();
  }

  /**
   * Turns a single interval into a range. Copies the start (preserving the
   * parent chain the user actually navigated, which generateStack would
   * rebuild canonically) and steps one interval along, so the new end is never
   * equal to the start — which #onStackChange() would collapse straight back.
   */
  addEndStack() {
    if (this.#endStack) {
      this.setFocus("end");
      return;
    }
    const endStack = copyStack(this.#stack);
    if (!endStack.shift(1) && !endStack.shift(-1)) return;
    this.#endStack = endStack;
    this.#focus = "end";
    this.#onStackChange();
  }

  /** Collapses the range back to its start. */
  removeEndStack() {
    if (!this.#endStack) return;
    this.#clearClamps();
    this.#endStack = null;
    this.#focus = "start";
    this.#onStackChange();
  }

  subscribe(callback: () => void) {
    this.#listeners.add(callback);
    return () => this.#listeners.delete(callback);
  }

  #notify() {
    for (const callback of this.#listeners) {
      callback();
    }
  }

  getStack() {
    return this.#stack;
  }

  getEndStack() {
    return this.#endStack;
  }

  #updateMinMax() {
    if (this.#endStack) {
      this.#stack.setMaxBeginSecond(this.#endStack.getEndSecond());
      this.#endStack.setMinEndSecond(this.#stack.getBeginSecond());
    } else {
      this.#stack.setMaxBeginSecond(undefined);
    }
  }

  #updateName() {
    this.#name = this.#stack.getName();
    if (this.#endStack) {
      this.#name += " - " + this.#endStack.getName();
    }
  }

  getSecondsRange(): [bigint, bigint] {
    const secondsRange: [bigint, bigint] = [
      this.#stack.getBeginSecond(),
      this.#stack.getEndSecond(),
    ];
    if (this.#endStack) {
      secondsRange[1] = this.#endStack.getEndSecond();
    }
    return secondsRange;
  }

  getName() {
    return this.#name;
  }
}

/// Create HGStacks from intervals
export default function useStacks(
  interval: HGInterval,
  endInterval: HGInterval | null,
) {
  const stacks = useRef<HGStacks>(new HGStacks(new HGStack(1n, "y"), null));

  // trigger rerender if stacks changed
  useSyncExternalStore(
    (callback) => stacks.current.subscribe(callback),
    () => stacks.current.getUpdateCounter(),
  );

  // initialize stacks -- deliberately run once: `interval`/`endInterval`
  // only seed the initial position, the stacks then navigate independently.
  useEffect(() => {
    if (!interval) return;
    stacks.current.setStack(generateStack(interval));
    if (endInterval) {
      stacks.current.setEndStack(generateStack(endInterval));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   if (
  //     !interval ||
  //     !stacks ||
  //     intervalsAreEqual(interval, stacks.getInterval())
  //   )
  //     return;

  //   constructStackFromInterval(interval, "").then((stack) => {
  //     stacks.setStack(stack);
  //   });
  // }, [interval?.it, interval?.n]);

  // useEffect(() => {
  //   if (!stacks) return;

  //   if (!endInterval) {
  //     stacks.setEndStack(null);
  //   } else if (!intervalsAreEqual(endInterval, stacks.getEndInterval())) {
  //     constructStackFromInterval(endInterval, "").then((endStack) => {
  //       stacks?.setEndStack(endStack);
  //     });
  //   }
  // }, [endInterval?.it, endInterval?.n]);

  return stacks.current;
}
